import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {Carousel} from 'react-responsive-carousel';
import {useTranslation} from 'react-i18next';

import ContentsHeader from '@/layouts/ContentsHeader';
import {patchReview, saveReview} from '@/services/community';
import {user} from '@/store/user';
import {switchNewLineTxt} from '@/utils';
import NoBrandLogo from '@/assets/banner/brand-default.svg';
import emptyKnewnew from '@/products/user_default.svg';
import {isOpenToast, ToastMessage} from '@/store';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

interface Props {
  mode: 'preview' | 'editor';
  setMode: (mode: 'preview' | 'editor') => void;
  imageList: string[];
  author: UserInfoObj;
  category: CategoryItem;
  title: string;
  contents: string;
  address: string;
  brand: BrandInfo;
  addressIds: number[];
  isTemp: boolean;
  removeTemp: () => void;
}

export default function CommunityPreview({
  mode,
  setMode,
  imageList,
  author,
  category,
  contents,
  address,
  brand,
  title,
  addressIds,
  isTemp,
  removeTemp,
}: Props) {
  const setIsOpenToast = useSetRecoilState(isOpenToast);
  const setToastMessage = useSetRecoilState(ToastMessage);
  const accessToken = useRecoilValue(user);
  const navigator = useNavigate();
  const {t} = useTranslation(['views'], {keyPrefix: 'community.Preview'});
  const makeCapital = (title: string | undefined) => {
    if (title) {
      const words = title.split('');
      words[0] = words[0].toUpperCase();
      return words.join('');
    }
    return '';
  };
  const updatePage = () => {
    const currentItem = document.querySelector('.slider');
    currentItem?.querySelector('.slide.selected');
  };
  const save = async () => {
    const path = window.location.pathname.split('/');
    const id = path[path.length - 1];

    const param: CommunityContentReq = {
      images: imageList.map((o, i) => {
        return {
          image: o,
          priority: i,
        };
      }),
      title,
      content: contents,
      category: category.param,
      address1: addressIds[0],
      address2: addressIds[1],
      brands: brand.id && brand.id > -1 ? brand.id : brand.title,
    };

    // 온라인 스토어 일  경우 address1, address2를 빼준다
    if (addressIds[0] === 9999) {
      param.address1 = null;
      param.address2 = null;
    }

    const res =
      id === 'add'
        ? await saveReview(param, accessToken)
        : await patchReview(param, accessToken, Number(id));
    setToastMessage('게시글이 성공적으로 등록되었습니다.');
    setIsOpenToast(true);
    navigator(`/community/${res.data.id}`, {
      state: {
        wrapperType: 'community',
        prev: 'community',
      },
    });

    if (isTemp) {
      removeTemp();
    }
  };
  return (
    <div
      className="community-contents-preview"
      style={{display: mode === 'preview' ? 'block' : 'none'}}>
      <ContentsHeader
        txt={'미리보기'}
        isOption={mode === 'editor' ? 'write' : 'none'}
        back={() => setMode('editor')}
      />
      <div className="community-detail-header contents-body">
        <div className="community-detail-imgs">
          {imageList.length > 0 && (
            <Carousel
              showArrows={false}
              showStatus={true}
              showThumbs={false}
              showIndicators={false}
              infiniteLoop={false}
              emulateTouch={true}
              onSwipeEnd={() => updatePage()}
              statusFormatter={(c, t) => {
                return `${c} / ${t}`;
              }}>
              {imageList.map((o, i) => {
                return (
                  <div key={`image_list_preview_${i}`} data-order={`${i + 1}`}>
                    <img src={`${o}`} alt="" />
                  </div>
                );
              })}
            </Carousel>
          )}
        </div>
      </div>
      <div className="contents-info">
        <div className="writer">
          <div
            className="profile"
            style={{
              background: `url(${
                author.profileImage ? author.profileImage : emptyKnewnew
              }) no-repeat center / cover`,
            }}></div>
          <div className="name">{author.nickname}</div>
        </div>
        <div className="content-category">{category ? category.txt : ''}</div>
      </div>
      <div className="contents-title">
        <div className="title">{title}</div>
        <div className="contents-sub-info">
          {t('contents_dummy_time')} • {address}
        </div>
      </div>
      <div
        className="detail-contents"
        dangerouslySetInnerHTML={{
          __html: contents ? switchNewLineTxt(contents || '') : '',
        }}></div>
      <div className="brands-divider"></div>
      <div className="brands">
        <div className="brands-info">
          <div
            className="brands-logo"
            style={{
              background: `url(${
                brand?.thumbnailUrl && brand?.thumbnailUrl.length > 0
                  ? brand?.thumbnailUrl
                  : NoBrandLogo
              }) no-repeat center / cover`,
            }}></div>
          {brand?.state === 'verified' ? (
            <div className="brand-txt">
              <div className="name">{brand?.title}</div>
              <div className="category" style={{textAlign: 'left'}}>
                {makeCapital(brand?.type)}
              </div>
            </div>
          ) : (
            <div className="non-verified-brand-txt">
              <div className="name">{brand?.title}</div>
            </div>
          )}
        </div>
      </div>
      <div className="divider preview-bottom"></div>
      <div
        className="contents-btn-wrapper preview"
        style={{background: 'rgba(255, 255, 255, 0.95)'}}>
        <button className="btn complete" onClick={() => save()}>
          {t('doneBtn')}
        </button>
      </div>
    </div>
  );
}
